import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ProductDataService from '../../services/product.service';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrencySync, initializeCurrency, getCurrencySymbolSync } from '../../utils/currencyUtils';
import { useTranslation } from '../../hooks/useTranslation';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currencyInitialized, setCurrencyInitialized] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
 

  useEffect(() => {
    // Initialize currency settings first, then fetch products
    const init = async () => {
      try {
        await initializeCurrency();
        // Force a re-render by checking currency symbol
        const symbol = getCurrencySymbolSync();
        console.log('Currency initialized with symbol:', symbol);
        setCurrencyInitialized(true);
        fetchProducts();
      } catch (error) {
        console.error('Error initializing currency:', error);
        setCurrencyInitialized(true); // Still set to true to avoid blocking render
        fetchProducts();
      }
    };
    init();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await ProductDataService.getAll();
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      showSnackbar(t('products.errorLoadingProducts') + ': ' + (error.response?.data?.message || error.message), 'error');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await ProductDataService.delete(deleteDialog.id);
      showSnackbar(t('products.productDeletedSuccess'));
      fetchProducts();
    } catch (error) {
      showSnackbar(t('products.errorDeletingProduct'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `http://localhost:8080/storage/${image}`;
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('products.title')}</Typography>
            {hasPermission('products.new') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/products/new')}
              >
                {t('products.newProduct')}
              </Button>
            )}
          </Box>

          <TextField
            placeholder={t('products.searchProducts')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('products.image')}</TableCell>
                  <TableCell>{t('products.name')}</TableCell>
                  <TableCell>{t('products.price')}</TableCell>
                  <TableCell>{t('products.Tracking')}</TableCell>
                  <TableCell align="center">{t('products.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProducts.map((product) => (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        {product.image_url || product.image ? (
                          <Avatar
                            src={getImageUrl(product.image_url || product.image)}
                            alt={product.name}
                            sx={{ width: 56, height: 56 }}
                            variant="rounded"
                          />
                        ) : (
                          <Avatar sx={{ width: 56, height: 56 }} variant="rounded">
                            No Image
                          </Avatar>
                        )}
                      </TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>
                        {formatCurrencySync(product.price)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.tracking || 'FIFO'}
                          size="small"
                          color={product.tracking === 'FIFO' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('products.edit') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/products/${product.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission('products.delete') && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(product.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this product?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductList;
